import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useState } from 'react';

import {
  Paper,
  Group,
  Box,
  Stack,
  Text,
  Title,
  Badge,
  Divider,
  SimpleGrid,
  Alert,
  LoadingOverlay,
  Button,
} from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';

import usePartner from '../hooks/partners/usePartner.ts';
import useRefuelingContract from '../hooks/refueling-contracts/use-refueling-contract.ts';
import useDownloadRefuelingContractAttachment from '../hooks/refueling-contracts/use-download-refueling-contract-attachment.ts';
import { formatDateTime2 } from '../utils/DateUtil.ts';
import { executeDownloadAttachment } from '../utils/attachment-utils.ts';

import type { DownloadRefuelingContractAttachmentRequest } from '../api/requests/refueling-contract-request.ts';
import type { Attachment } from '../models/attachment.ts';

import PreviousPageButton from '../components/ui/PreviousPageButton.tsx';
import {
  RECEIPT_MODE_LABEL,
  REFUELING_CONTRACT_DISBURSEMENT_FEE_PROCESSING_METHOD_LABEL,
  GAS_STATION_ACCOUNT_OWNER_TYPE_LABEL,
} from '../const/labels.ts';

export default function RefuelingContractInspectPage() {
  const navigate = useNavigate();
  const { state } = useLocation();

  const { partnerId, id } = useParams();
  const { partner, isLoading: isPartnerLoading } = usePartner(partnerId);
  const { refuelingContract, isLoading: isContractLoading } =
    useRefuelingContract(partnerId, id);
  const { mutate: downloadAttachment, isPending: isDownloadAttachmentPending } =
    useDownloadRefuelingContractAttachment();

  const [isPublishedNoticeClosed, setPublishedNoticeClosed] = useState(false);

  if (isPartnerLoading || isContractLoading) {
    return (
      <LoadingOverlay visible loaderProps={{ color: 'cyan', type: 'bars' }} />
    );
  }

  // constants
  const shouldDisplayPublishedNotice: boolean =
    !!state?.published && !isPublishedNoticeClosed;
  const canDownload: boolean = refuelingContract.attachments.length === 1;

  const lastPagePath: string = `/partners/${partnerId}`;
  const newPagePath: string = `/partners/${partnerId}/refueling-contracts/new`;

  // callback functions
  function onDownloadAttachment(): void {
    if (isDownloadAttachmentPending) return;

    const attachment: Attachment = refuelingContract.attachments[0];
    const request: DownloadRefuelingContractAttachmentRequest = {
      partnerId: partnerId,
      contractId: refuelingContract.id,
      attachmentId: attachment.id,
    };

    downloadAttachment(request, {
      onSuccess: (downloadUrl) => {
        executeDownloadAttachment(downloadUrl, attachment.displayName);
      },
    });
  }
  function onDuplicate(): void {
    navigate(newPagePath, { state: { cloneId: refuelingContract.id } });
  }

  return (
    <>
      <PreviousPageButton href={lastPagePath} content={'返回業者詳情頁'} />
      {shouldDisplayPublishedNotice && (
        <Alert
          variant="light"
          color="green"
          withCloseButton
          mb={20}
          icon={<IconInfoCircle />}
          onClose={() => setPublishedNoticeClosed(true)}
          title="成功發佈合約"
        />
      )}

      <Stack gap="lg" mb={70}>
        <Paper radius="lg" p="xl" shadow="xs">
          <Group justify="space-between" align="center">
            <Box>
              <Title order={3}>{refuelingContract.name}</Title>
              <Text fz={14} c="gray">
                {`${partner.name} | 加油站合約`}
              </Text>
            </Box>
            <Badge>
              <Text size="xs" fw={700}>
                合約已發佈
              </Text>
            </Badge>
          </Group>
        </Paper>

        <Paper radius="lg" p="xl" shadow="xs">
          <Text fw={500} fz={14}>
            基本資訊
          </Text>
          <Divider mt={10} mb={20} />
          <SimpleGrid cols={3} verticalSpacing="xl">
            <Box>
              <Text c="gray" fz={14} fw={500} pb={10}>
                汽油分潤條件
              </Text>
              <Text fz={18} fw={400}>
                {`${refuelingContract.gasBaseDiscount} 公升/元`}
              </Text>
            </Box>
            <Box>
              <Text c="gray" fz={14} fw={500} pb={10}>
                柴油分潤條件
              </Text>
              <Text fz={18} fw={400}>
                {`${refuelingContract.dieselBaseDiscount} 公升/元`}
              </Text>
            </Box>
            <Box>
              <Text c="gray" fz={14} fw={500} pb={10}>
                合約生效起迄日
              </Text>
              <Text
                fz={18}
                fw={400}
              >{`${formatDateTime2(refuelingContract.startAt)} - ${formatDateTime2(refuelingContract.endAt)}`}</Text>
            </Box>
            <Box>
              <Text c="gray" fz={14} fw={500} pb={10}>
                手續費扣除方式
              </Text>
              <Text fz={18} fw={400}>
                {
                  REFUELING_CONTRACT_DISBURSEMENT_FEE_PROCESSING_METHOD_LABEL[
                    refuelingContract.disbursementFeeProcessingMethod
                  ]
                }
              </Text>
            </Box>
            <Box>
              <Text c="gray" fz={14} fw={500} pb={10}>
                發票開立方式
              </Text>
              <Text fz={18} fw={400}>
                {RECEIPT_MODE_LABEL[refuelingContract.receiptMode]}
              </Text>
            </Box>
          </SimpleGrid>
        </Paper>

        <Paper radius="lg" p="xl" shadow="xs">
          <Text fw={500} fz={14}>
            現銷金資訊
          </Text>
          <Divider mt={10} mb={20} />
          <SimpleGrid cols={3} verticalSpacing="xl">
            <Box>
              <Text c="gray" fz={14} fw={500} pb={10}>
                現銷金帳戶類型
              </Text>
              <Text fz={18} fw={400}>
                {
                  GAS_STATION_ACCOUNT_OWNER_TYPE_LABEL[
                    refuelingContract.gasStationAccountOwnerType
                  ]
                }
              </Text>
            </Box>
            {refuelingContract.gasStationAccountOwnerType !== 'JustLog' && (
              <>
                <Box>
                  <Text c="gray" fz={14} fw={500} pb={10}>
                    自動補款門檻
                  </Text>
                  <Text fz={18} fw={400}>
                    {refuelingContract.gasStationAccountThreshold}
                  </Text>
                </Box>
                <Box>
                  <Text c="gray" fz={14} fw={500} pb={10}>
                    自動補款金額
                  </Text>
                  <Text fz={18} fw={400}>
                    {refuelingContract.gasStationAccountAutoDepositAmount}
                  </Text>
                </Box>
              </>
            )}
          </SimpleGrid>
        </Paper>

        <Paper radius="lg" p="xl" shadow="xs">
          <Text fw={500} fz={14}>
            交易通知網址
          </Text>
          <Divider mt={10} mb={20} />
          <SimpleGrid cols={3} verticalSpacing="xl">
            <Box>
              <Text c="gray" fz={14} fw={500} pb={10}>
                連鎖店交易通知網址
              </Text>
              <Text fz={18} fw={400}>
                {!refuelingContract.gasStationNotificationEndpoint
                  ? '-'
                  : refuelingContract.gasStationNotificationEndpoint}
              </Text>
            </Box>
          </SimpleGrid>
        </Paper>
      </Stack>

      {/* Footer */}
      <Box
        pos="fixed"
        bottom={0}
        left={0}
        right={0}
        h={70}
        p="md"
        bg="var(--mantine-color-white)"
        style={{
          borderTop: '1px solid var(--mantine-color-gray-3)',
          zIndex: 100,
        }}
      >
        <Group justify="flex-end">
          <Button
            variant="outline"
            fw={400}
            onClick={onDownloadAttachment}
            disabled={!canDownload}
          >
            下載紙本合約
          </Button>
          <Button variant="outline" fw={400} onClick={onDuplicate}>
            複製合約
          </Button>
        </Group>
      </Box>
    </>
  );
}
